/**
 * Bot opponents. Lower levels sample from Stockfish's top lines with a
 * softmax over centipawn loss, so their mistakes look like human mistakes
 * (a slightly worse plan, a missed tactic) instead of the random collapses a
 * strength-limited engine produces. Upper levels use Stockfish's own Elo limiter.
 */
import { Chess } from "chess.js";
import { scoreToCp, type Score } from "./uci";
import type { StockfishEngine } from "./engine";

export type BotLevel = {
  id: string;
  name: string;
  rating: number;
  blurb: string;
  kind: "heuristic" | "human" | "limited" | "full";
  depth?: number;
  multipv?: number;
  /** Softmax temperature in centipawns: higher means looser play. */
  temperature?: number;
  /** Chance per move of picking any of the candidate lines uniformly. */
  slipRate?: number;
  elo?: number;
  movetime?: number;
  thinkMs: [number, number];
};

export const BOT_LEVELS: BotLevel[] = [
  { id: "pawn", name: "Beginner", rating: 400, blurb: "Learn the basics.", kind: "heuristic", thinkMs: [400, 900] },
  { id: "knight", name: "Easy", rating: 800, blurb: "A relaxed game.", kind: "human", depth: 6, multipv: 6, temperature: 140, slipRate: 0.12, thinkMs: [500, 1300] },
  { id: "bishop", name: "Medium", rating: 1200, blurb: "A balanced challenge.", kind: "human", depth: 8, multipv: 5, temperature: 70, slipRate: 0.06, thinkMs: [600, 1500] },
  { id: "rook", name: "Hard", rating: 1600, blurb: "Think a few moves ahead.", kind: "human", depth: 10, multipv: 4, temperature: 32, slipRate: 0.025, thinkMs: [700, 1700] },
  { id: "queen", name: "Expert", rating: 2000, blurb: "Few chances for mistakes.", kind: "limited", elo: 2000, movetime: 700, thinkMs: [700, 1400] },
  { id: "king", name: "Master", rating: 2500, blurb: "Precise, demanding play.", kind: "limited", elo: 2500, movetime: 1000, thinkMs: [900, 1600] },
  { id: "stockfish", name: "Maximum", rating: 3200, blurb: "The engine at full strength.", kind: "full", movetime: 1500, thinkMs: [0, 0] },
];

export function getLevel(id: string): BotLevel {
  return BOT_LEVELS.find((l) => l.id === id) ?? BOT_LEVELS[2];
}

/** Deterministic PRNG for tests and reproducible games. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type Candidate = { uci: string; cp: number };

/**
 * Picks a move from candidates whose `cp` is from the mover's point of view.
 * Candidates are sorted best-first by the caller (Stockfish MultiPV order).
 */
export function pickHumanMove(cands: Candidate[], temperature: number, slipRate: number, rng: () => number): string {
  if (cands.length === 0) throw new Error("no candidates");
  if (cands.length === 1) return cands[0].uci;
  if (rng() < slipRate) return cands[Math.floor(rng() * cands.length)].uci;
  const best = Math.max(...cands.map((c) => c.cp));
  const weights = cands.map((c) => Math.exp(-(best - c.cp) / Math.max(1, temperature)));
  const total = weights.reduce((a, b) => a + b, 0);
  let r = rng() * total;
  for (let i = 0; i < cands.length; i++) {
    r -= weights[i];
    if (r <= 0) return cands[i].uci;
  }
  return cands[0].uci;
}

const VALUE: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };

/** The 400-rated bot: greedy, noisy, and blind to what the opponent threatens. */
export function pickBeginnerMove(fen: string, rng: () => number): string {
  const chess = new Chess(fen);
  const moves = chess.moves({ verbose: true });
  if (moves.length === 0) throw new Error("no legal moves");
  const mate = moves.find((m) => m.san.endsWith("#"));
  if (mate && rng() < 0.5) return mate.lan;
  if (rng() < 0.3) return moves[Math.floor(rng() * moves.length)].lan;
  let bestScore = -Infinity;
  let best = moves[0];
  for (const m of moves) {
    let s = rng() * 3;
    if (m.captured) s += VALUE[m.captured] * 1.5;
    if (m.san.includes("+")) s += 0.7;
    if (m.promotion) s += 6;
    if (m.piece === "k" && !m.san.startsWith("O-")) s -= 1;
    if (s > bestScore) {
      bestScore = s;
      best = m;
    }
  }
  return best.lan;
}

/** Mover-POV centipawns, with mates clamped so they still dominate the softmax. */
export function moverCp(whitePov: Score, sideToMove: "w" | "b"): number {
  const cp = Math.max(-3000, Math.min(3000, scoreToCp(whitePov)));
  return sideToMove === "w" ? cp : -cp;
}

export async function chooseBotMove(
  fen: string,
  level: BotLevel,
  /** Called only by the levels that use Stockfish, so Beginner never downloads it. */
  engine: () => StockfishEngine,
  rng: () => number = Math.random,
  signal?: AbortSignal,
): Promise<string> {
  const stm = (fen.split(" ")[1] ?? "w") as "w" | "b";
  switch (level.kind) {
    case "heuristic":
      return pickBeginnerMove(fen, rng);
    case "human": {
      const res = await engine().analyse(fen, { depth: level.depth, multipv: level.multipv, signal });
      const cands = res.lines
        .filter((l) => l.pv.length > 0)
        .map((l) => ({ uci: l.pv[0], cp: moverCp(l.score, stm) }));
      if (cands.length === 0 && res.bestmove) return res.bestmove;
      return pickHumanMove(cands, level.temperature ?? 50, level.slipRate ?? 0, rng);
    }
    case "limited": {
      const res = await engine().analyse(fen, { elo: level.elo, movetime: level.movetime, signal });
      if (!res.bestmove) throw new Error("engine returned no move");
      return res.bestmove;
    }
    case "full": {
      const res = await engine().analyse(fen, { movetime: level.movetime, signal });
      if (!res.bestmove) throw new Error("engine returned no move");
      return res.bestmove;
    }
  }
}
